import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FiFlag, FiChevronLeft, FiChevronRight, FiAlertTriangle, FiClock, FiCheckCircle, FiGrid } from 'react-icons/fi';
import { attempts as attemptsApi } from '../../api';
import io from 'socket.io-client';
import toast from 'react-hot-toast';

// ─── Timer Component ───────────────────────────────────────────────────────────
const ExamTimer = ({ startedAt, durationMinutes, onTimeUp }) => {
  const [remaining, setRemaining] = useState(null);

  useEffect(() => {
    const calc = () => {
      const end = new Date(startedAt).getTime() + durationMinutes * 60 * 1000;
      const diff = Math.max(0, Math.floor((end - Date.now()) / 1000));
      setRemaining(diff);
      if (diff === 0) onTimeUp();
    };
    calc();
    const t = setInterval(calc, 1000);
    return () => clearInterval(t);
  }, [startedAt, durationMinutes, onTimeUp]);

  if (remaining === null) return null;
  const h = Math.floor(remaining / 3600);
  const m = Math.floor((remaining % 3600) / 60);
  const s = remaining % 60;
  const urgent = remaining < 300; // last 5 minutes
  const warning = remaining < 600; // last 10 minutes

  return (
    <div className={`flex items-center gap-2 px-4 py-2 rounded-xl font-mono font-bold text-lg transition-colors
      ${urgent ? 'bg-red-100 text-red-700 animate-pulse' : warning ? 'bg-amber-50 text-amber-700' : 'bg-navy/10 text-navy'}`}>
      <FiClock size={16} />
      {h > 0 ? `${String(h).padStart(2,'0')}:` : ''}
      {String(m).padStart(2,'0')}:{String(s).padStart(2,'0')}
    </div>
  );
};

// ─── Question Navigation Panel ─────────────────────────────────────────────────
const QuestionNav = ({ questions, answers, currentIdx, onNavigate, onClose }) => (
  <div className="flex flex-col h-full">
    <div className="flex items-center justify-between p-4 border-b border-gray-100">
      <h3 className="font-bold text-gray-900 text-sm">Question Navigator</h3>
      <button onClick={onClose} className="p-1 rounded-lg hover:bg-gray-100 lg:hidden text-gray-500">✕</button>
    </div>
    <div className="p-4 flex-1 overflow-y-auto">
      <div className="flex flex-wrap gap-2 mb-4">
        {[
          { color: 'bg-navy border-navy text-white', label: 'Answered' },
          { color: 'bg-amber-400 border-amber-400 text-white', label: 'Review' },
          { color: 'border-gray-200 text-gray-600', label: 'Not answered' },
        ].map(({ color, label }) => (
          <div key={label} className="flex items-center gap-1.5 text-xs">
            <span className={`w-5 h-5 rounded border-2 flex-shrink-0 ${color}`} />
            <span className="text-gray-500">{label}</span>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-5 gap-1.5">
        {questions.map((q, i) => {
          const ans = answers[q.id];
          const isAnswered = ans?.is_answered;
          const isReview = ans?.is_marked_for_review;
          const isCurrent = i === currentIdx;
          return (
            <button key={q.id} onClick={() => onNavigate(i)}
              className={`q-nav-btn ${isCurrent ? 'current' : ''} ${isReview ? 'review' : isAnswered ? 'answered' : ''}`}>
              {i + 1}
            </button>
          );
        })}
      </div>
    </div>
    <div className="p-4 border-t border-gray-100 text-xs text-gray-500 space-y-1">
      <p>Answered: <strong>{Object.values(answers).filter(a => a?.is_answered).length}</strong> / {questions.length}</p>
      <p>Marked for review: <strong>{Object.values(answers).filter(a => a?.is_marked_for_review).length}</strong></p>
    </div>
  </div>
);

// ─── Submit Confirmation Modal ─────────────────────────────────────────────────
const SubmitModal = ({ questions, answers, onConfirm, onCancel, submitting }) => {
  const answered = Object.values(answers).filter(a => a?.is_answered).length;
  const review = Object.values(answers).filter(a => a?.is_marked_for_review).length;
  const unanswered = questions.length - answered;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-sm animate-slide-up">
        <div className="w-14 h-14 bg-amber-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
          <FiAlertTriangle size={26} className="text-amber-600" />
        </div>
        <h2 className="text-xl font-black text-gray-900 text-center mb-2">Submit Exam?</h2>
        <p className="text-gray-500 text-sm text-center mb-5">This action cannot be undone.</p>
        <div className="bg-gray-50 rounded-xl p-4 space-y-2 mb-5 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-500">Total Questions</span>
            <span className="font-bold">{questions.length}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-emerald-600">Answered</span>
            <span className="font-bold text-emerald-600">{answered}</span>
          </div>
          {review > 0 && (
            <div className="flex justify-between">
              <span className="text-amber-600">Marked for Review</span>
              <span className="font-bold text-amber-600">{review}</span>
            </div>
          )}
          {unanswered > 0 && (
            <div className="flex justify-between">
              <span className="text-red-500">Unanswered</span>
              <span className="font-bold text-red-500">{unanswered}</span>
            </div>
          )}
        </div>
        <div className="flex gap-3">
          <button onClick={onCancel} className="btn-secondary flex-1" disabled={submitting}>Go Back</button>
          <button onClick={onConfirm} className="btn-primary flex-1" disabled={submitting}>
            {submitting ? <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <FiCheckCircle size={15} />}
            {submitting ? 'Submitting...' : 'Submit'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Violation Warning Modal ───────────────────────────────────────────────────
const ViolationModal = ({ count, maxViolations, onDismiss }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
    <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-sm border-2 border-red-300 animate-slide-up">
      <div className="w-14 h-14 bg-red-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
        <FiAlertTriangle size={26} className="text-red-600" />
      </div>
      <h2 className="text-xl font-black text-red-700 text-center mb-2">⚠ Warning!</h2>
      <p className="text-gray-700 text-sm text-center mb-3">
        Suspicious activity detected. Violation <strong>{count}</strong> of <strong>{maxViolations}</strong>.
      </p>
      {count >= maxViolations - 1 && (
        <p className="text-red-600 text-xs text-center font-bold bg-red-50 p-2 rounded-lg mb-4">
          Next violation will auto-submit your exam!
        </p>
      )}
      <button onClick={onDismiss} className="btn-danger w-full">I Understand</button>
    </div>
  </div>
);

// ─── Main Exam Interface ───────────────────────────────────────────────────────
export default function ExamInterface() {
  const { examId } = useParams();
  const navigate = useNavigate();

  const [state, setState] = useState('loading'); // loading | instructions | exam | submitting | result
  const [exam, setExam] = useState(null);
  const [attempt, setAttempt] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [currentIdx, setCurrentIdx] = useState(0);
  const [showNav, setShowNav] = useState(false);
  const [showSubmit, setShowSubmit] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [violationCount, setViolationCount] = useState(0);
  const [showViolation, setShowViolation] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);

  const MAX_VIOLATIONS = 3;
  const socketRef = useRef(null);
  const saveTimerRef = useRef(null);
  const questionTimeRef = useRef({});
  const questionStartRef = useRef(Date.now());

  // ── Load exam ──
  useEffect(() => {
    attemptsApi.start(examId)
      .then(res => {
        const { attempt: att, exam: ex, isResumed } = res.data.data;
        setAttempt(att);
        setExam(ex);
        setQuestions(ex.questions || []);
        setViolationCount(att.violation_count || 0);

        // Pre-populate answers from resumed state
        const initialAnswers = {};
        (ex.questions || []).forEach(q => {
          initialAnswers[q.id] = {
            is_answered: !!q.selected_answer,
            is_marked_for_review: q.is_marked_for_review || false,
            selected_answer: q.selected_answer || null,
          };
        });
        setAnswers(initialAnswers);

        if (isResumed) {
          toast.success('Resuming your exam...', { icon: '▶️' });
        }
        setState(isResumed ? 'exam' : 'instructions');
      })
      .catch(err => {
        toast.error(err.response?.data?.message || 'Failed to start exam');
        navigate('/candidate/exams');
      });
  }, [examId, navigate]);

  // ── Socket connection ──
  useEffect(() => {
    if (!attempt) return;
    const token = localStorage.getItem('accessToken');
    const socket = io(process.env.REACT_APP_WS_URL || 'http://localhost:5000', { auth: { token } });
    socketRef.current = socket;
    socket.emit('join_exam', examId);

    socket.on('admin_message', (d) => {
      toast(d.message, { icon: '📢', duration: 8000 });
    });

    return () => socket.disconnect();
  }, [attempt, examId]);

  // ── Anti-cheating: tab visibility ──
  useEffect(() => {
    if (!attempt || state !== 'exam') return;

    const handleVisibilityChange = () => {
      if (document.hidden) reportViolation('tab_switch', 'Candidate switched tab or minimized window');
    };
    const handleBlur = () => reportViolation('window_blur', 'Window focus lost');
    const handleContextMenu = (e) => e.preventDefault();
    const handleCopy = (e) => e.preventDefault();
    const handlePaste = (e) => e.preventDefault();
    const handleKeyDown = (e) => {
      // Block F12, Ctrl+Shift+I, Ctrl+U etc.
      if (e.key === 'F12' || (e.ctrlKey && e.shiftKey && ['I','J','C'].includes(e.key)) ||
          (e.ctrlKey && e.key === 'u')) {
        e.preventDefault();
        reportViolation('devtools_attempt', 'Attempted to open developer tools');
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    document.addEventListener('contextmenu', handleContextMenu);
    document.addEventListener('copy', handleCopy);
    document.addEventListener('paste', handlePaste);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      document.removeEventListener('contextmenu', handleContextMenu);
      document.removeEventListener('copy', handleCopy);
      document.removeEventListener('paste', handlePaste);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [attempt, state]);

  const reportViolation = useCallback(async (type, detail) => {
    if (!attempt || state !== 'exam') return;
    try {
      const res = await attemptsApi.reportViolation(attempt.id, { type, detail });
      const newCount = res.data.violationCount;
      setViolationCount(newCount);
      if (res.data.shouldAutoSubmit) {
        toast.error('Exam auto-submitted due to multiple violations!', { duration: 5000 });
        navigate('/candidate/results');
        return;
      }
      setShowViolation(true);

      // Notify via socket
      if (socketRef.current) {
        socketRef.current.emit('progress_update', { examId, answeredCount: Object.values(answers).filter(a => a?.is_answered).length });
      }
    } catch {}
  }, [attempt, state, examId, answers, navigate]);

  // ── Auto-save answer ──
  const saveAnswer = useCallback(async (questionId, selectedAnswer, isMarkedForReview) => {
    if (!attempt) return;
    const timeSpent = Math.floor((Date.now() - (questionTimeRef.current[questionId] || questionStartRef.current)) / 1000);

    try {
      await attemptsApi.saveAnswer(attempt.id, {
        questionId, selectedAnswer, isMarkedForReview,
        timeSpent: (questionTimeRef.current[`time_${questionId}`] || 0) + timeSpent,
      });
      setLastSaved(new Date());
    } catch {}
  }, [attempt]);

  // ── Handle answer selection ──
  const handleAnswer = useCallback((questionId, value, type) => {
    setAnswers(prev => {
      let newAnswer;
      if (type === 'multi_correct') {
        const current = (prev[questionId]?.selected_answer || '').split(',').filter(Boolean);
        const updated = current.includes(value)
          ? current.filter(v => v !== value)
          : [...current, value].sort();
        newAnswer = { ...prev[questionId], selected_answer: updated.join(','), is_answered: updated.length > 0 };
      } else {
        newAnswer = { ...prev[questionId], selected_answer: value, is_answered: true };
      }
      // Debounced save
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      saveTimerRef.current = setTimeout(() => saveAnswer(questionId, newAnswer.selected_answer, newAnswer.is_marked_for_review), 800);
      return { ...prev, [questionId]: newAnswer };
    });
  }, [saveAnswer]);

  const toggleReview = useCallback((questionId) => {
    setAnswers(prev => {
      const updated = { ...prev[questionId], is_marked_for_review: !prev[questionId]?.is_marked_for_review };
      saveAnswer(questionId, updated.selected_answer, updated.is_marked_for_review);
      return { ...prev, [questionId]: updated };
    });
  }, [saveAnswer]);

  const navigateTo = (idx) => {
    const current = questions[currentIdx];
    if (current) questionTimeRef.current[`time_${current.id}`] = (questionTimeRef.current[`time_${current.id}`] || 0) + Math.floor((Date.now() - questionStartRef.current) / 1000);
    questionStartRef.current = Date.now();
    setCurrentIdx(idx);
    setShowNav(false);
    // Emit progress
    if (socketRef.current) {
      socketRef.current.emit('progress_update', { examId, answeredCount: Object.values(answers).filter(a => a?.is_answered).length, currentQuestion: idx + 1 });
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const res = await attemptsApi.submit(attempt.id);
      setResult(res.data.data);
      setState('result');
      setShowSubmit(false);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Submit failed');
      setSubmitting(false);
    }
  };

  const handleTimeUp = useCallback(() => {
    toast.error('Time is up! Auto-submitting...', { duration: 3000 });
    setTimeout(() => handleSubmit(), 2000);
  }, []);

  // ── LOADING STATE ──
  if (state === 'loading') return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center">
        <div className="w-12 h-12 border-4 border-navy/20 border-t-navy rounded-full animate-spin mx-auto mb-4" />
        <p className="text-gray-500 font-medium">Loading exam...</p>
      </div>
    </div>
  );

  // ── INSTRUCTIONS STATE ──
  if (state === 'instructions') return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg p-8 animate-slide-up">
        <div className="w-14 h-14 bg-navy/10 rounded-2xl flex items-center justify-center mx-auto mb-5">
          <span className="text-navy text-2xl">📋</span>
        </div>
        <h1 className="text-2xl font-black text-gray-900 text-center mb-1">{exam?.title}</h1>
        <p className="text-gray-500 text-sm text-center mb-6">{exam?.description}</p>

        <div className="grid grid-cols-2 gap-3 mb-6">
          {[
            ['⏱ Duration', `${exam?.duration_minutes} minutes`],
            ['❓ Questions', exam?.total_questions],
            ['✅ Passing', `${exam?.passing_marks}%`],
            ['📊 Max Marks', exam?.max_marks],
          ].map(([label, value]) => (
            <div key={label} className="bg-gray-50 rounded-xl p-3 text-center">
              <p className="text-gray-500 text-xs">{label}</p>
              <p className="font-bold text-gray-900 mt-0.5">{value}</p>
            </div>
          ))}
        </div>

        {exam?.instructions && (
          <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 mb-5">
            <p className="text-xs font-bold text-blue-700 mb-2">Instructions:</p>
            <p className="text-sm text-blue-800 whitespace-pre-wrap">{exam.instructions}</p>
          </div>
        )}

        <div className="bg-red-50 border border-red-100 rounded-xl p-4 mb-6">
          <p className="text-xs font-bold text-red-700 mb-2">⚠ Important Rules:</p>
          <ul className="text-xs text-red-700 space-y-1 list-disc list-inside">
            <li>Do not switch tabs or minimize the window</li>
            <li>Do not right-click or copy text</li>
            <li>After {MAX_VIOLATIONS} violations, exam will be auto-submitted</li>
            <li>Exam auto-submits when time runs out</li>
          </ul>
        </div>

        <button onClick={() => setState('exam')} className="btn-primary w-full py-3 text-base">
          Start Exam →
        </button>
      </div>
    </div>
  );

  // ── RESULT STATE ──
  if (state === 'result') {
    if (result?.submitted) return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="bg-white rounded-2xl shadow-xl p-10 text-center max-w-sm animate-slide-up">
          <div className="text-5xl mb-4">✅</div>
          <h2 className="text-2xl font-black text-gray-900 mb-2">Submitted!</h2>
          <p className="text-gray-500 mb-6">Results will be announced by the admin.</p>
          <button onClick={() => navigate('/candidate')} className="btn-primary">Go to Dashboard</button>
        </div>
      </div>
    );

    const pct = parseFloat(result?.percentage || 0);
    const circumference = 2 * Math.PI * 54;
    const offset = circumference - (pct / 100) * circumference;

    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-md text-center animate-slide-up">
          <h2 className="text-2xl font-black text-gray-900 mb-6">Exam Complete!</h2>
          <div className="relative w-36 h-36 mx-auto mb-6">
            <svg className="w-full h-full -rotate-90" viewBox="0 0 120 120">
              <circle cx="60" cy="60" r="54" fill="none" stroke="#f0f0f0" strokeWidth="10" />
              <circle cx="60" cy="60" r="54" fill="none"
                stroke={result?.is_passed ? '#10b981' : '#ef4444'}
                strokeWidth="10" strokeLinecap="round"
                strokeDasharray={circumference}
                strokeDashoffset={offset}
                style={{ transition: 'stroke-dashoffset 1.5s ease-in-out' }} />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className="text-3xl font-black text-gray-900">{pct.toFixed(1)}%</span>
            </div>
          </div>
          <div className={`text-3xl font-black mb-4 ${result?.is_passed ? 'text-emerald-600' : 'text-red-600'}`}>
            {result?.is_passed ? '🎉 PASSED!' : '😔 FAILED'}
          </div>
          <div className="grid grid-cols-3 gap-3 mb-6 bg-gray-50 p-4 rounded-xl text-sm">
            <div><p className="text-gray-400 text-xs">Score</p><p className="font-black">{parseFloat(result?.raw_score || 0).toFixed(1)}</p></div>
            <div><p className="text-gray-400 text-xs">Correct</p><p className="font-black text-emerald-600">{result?.correct_answers}</p></div>
            <div><p className="text-gray-400 text-xs">Wrong</p><p className="font-black text-red-500">{result?.wrong_answers}</p></div>
          </div>
          <div className="flex gap-3">
            <button onClick={() => navigate('/candidate')} className="btn-secondary flex-1 text-sm">Dashboard</button>
            <button onClick={() => navigate(`/candidate/results/${attempt.id}`)} className="btn-primary flex-1 text-sm">View Details</button>
          </div>
        </div>
      </div>
    );
  }

  // ── EXAM STATE ──
  const currentQuestion = questions[currentIdx];
  const currentAnswer = answers[currentQuestion?.id] || {};
  const totalAnswered = Object.values(answers).filter(a => a?.is_answered).length;
  const progress = questions.length > 0 ? (totalAnswered / questions.length) * 100 : 0;

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col select-none" style={{ userSelect: 'none' }}>
      {/* Modals */}
      {showSubmit && (
        <SubmitModal questions={questions} answers={answers}
          onConfirm={handleSubmit} onCancel={() => setShowSubmit(false)} submitting={submitting} />
      )}
      {showViolation && (
        <ViolationModal count={violationCount} maxViolations={MAX_VIOLATIONS}
          onDismiss={() => setShowViolation(false)} />
      )}

      {/* Header */}
      <header className="bg-white border-b border-gray-100 px-4 py-3 flex items-center gap-3 sticky top-0 z-30 shadow-sm">
        <div className="flex-1 min-w-0">
          <p className="font-bold text-gray-900 truncate text-sm">{exam?.title}</p>
          <p className="text-xs text-gray-400">{totalAnswered}/{questions.length} answered</p>
        </div>
        <ExamTimer
          startedAt={attempt?.started_at}
          durationMinutes={exam?.duration_minutes}
          onTimeUp={handleTimeUp}
        />
        {violationCount > 0 && (
          <div className="flex items-center gap-1 bg-red-50 text-red-600 px-2 py-1 rounded-lg text-xs font-bold">
            <FiAlertTriangle size={12} />
            {violationCount}/{MAX_VIOLATIONS}
          </div>
        )}
        {lastSaved && (
          <span className="hidden lg:block text-xs text-gray-300">
            Saved {new Date(lastSaved).toLocaleTimeString()}
          </span>
        )}
        <button onClick={() => setShowNav(s => !s)} className="p-2 rounded-lg hover:bg-gray-100 lg:hidden">
          <FiGrid size={18} />
        </button>
        <button onClick={() => setShowSubmit(true)} className="btn-primary text-sm py-2">
          <FiCheckCircle size={14} /> Submit
        </button>
      </header>

      {/* Progress bar */}
      <div className="w-full h-1 bg-gray-100">
        <div className="h-1 bg-navy transition-all duration-300" style={{ width: `${progress}%` }} />
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* Left nav panel - desktop */}
        <aside className="hidden lg:flex flex-col w-56 bg-white border-r border-gray-100 overflow-hidden">
          <QuestionNav
            questions={questions} answers={answers}
            currentIdx={currentIdx} onNavigate={navigateTo} onClose={() => {}} />
        </aside>

        {/* Mobile nav overlay */}
        {showNav && (
          <div className="fixed inset-0 z-40 lg:hidden">
            <div className="absolute inset-0 bg-black/40" onClick={() => setShowNav(false)} />
            <aside className="absolute left-0 top-0 bottom-0 w-64 bg-white shadow-xl z-50 flex flex-col">
              <QuestionNav questions={questions} answers={answers}
                currentIdx={currentIdx} onNavigate={navigateTo} onClose={() => setShowNav(false)} />
            </aside>
          </div>
        )}

        {/* Main question area */}
        <main className="flex-1 overflow-y-auto">
          <div className="max-w-2xl mx-auto p-4 lg:p-8">
            {currentQuestion && (
              <div className="animate-fade-in">
                {/* Question header */}
                <div className="flex items-start justify-between gap-3 mb-5">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs font-bold bg-navy/10 text-navy px-2.5 py-1 rounded-full">
                      Q {currentIdx + 1} / {questions.length}
                    </span>
                    {currentQuestion.difficulty && (
                      <span className={`text-xs font-bold px-2.5 py-1 rounded-full capitalize ${
                        currentQuestion.difficulty === 'easy' ? 'bg-emerald-50 text-emerald-700' :
                        currentQuestion.difficulty === 'hard' ? 'bg-red-50 text-red-700' :
                        'bg-amber-50 text-amber-700'}`}>
                        {currentQuestion.difficulty}
                      </span>
                    )}
                    {currentQuestion.category && (
                      <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">{currentQuestion.category}</span>
                    )}
                    {currentQuestion.marks && (
                      <span className="text-xs text-gray-400">{currentQuestion.marks} mark{currentQuestion.marks !== 1 ? 's' : ''}</span>
                    )}
                  </div>
                  <button
                    onClick={() => toggleReview(currentQuestion.id)}
                    className={`flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg border-2 transition-colors flex-shrink-0
                      ${currentAnswer.is_marked_for_review
                        ? 'bg-amber-400 border-amber-400 text-white'
                        : 'border-gray-200 text-gray-500 hover:border-amber-400 hover:text-amber-600'}`}>
                    <FiFlag size={12} />
                    {currentAnswer.is_marked_for_review ? 'Marked' : 'Mark Review'}
                  </button>
                </div>

                {/* Question text */}
                <div className="card p-6 mb-5">
                  <p className="text-gray-900 text-base lg:text-lg leading-relaxed font-medium">
                    {currentQuestion.question_text}
                  </p>
                  {currentQuestion.image_url && (
                    <img src={currentQuestion.image_url} alt="Question" className="mt-4 max-w-full rounded-xl" />
                  )}
                </div>

                {/* Answer options */}
                {currentQuestion.question_type === 'true_false' ? (
                  <div className="grid grid-cols-2 gap-3">
                    {['true', 'false'].map((option) => (
                      <button key={option}
                        onClick={() => handleAnswer(currentQuestion.id, option, 'true_false')}
                        className={`exam-option p-5 rounded-2xl border-2 font-bold text-lg capitalize transition-all
                          ${currentAnswer.selected_answer === option ? 'selected' : 'bg-white border-gray-200 text-gray-700 hover:border-navy/40'}`}>
                        {option === 'true' ? '✓ True' : '✗ False'}
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="space-y-3">
                    {['a', 'b', 'c', 'd'].filter(o => currentQuestion[`option_${o}`]).map((option) => {
                      const isSelected = currentQuestion.question_type === 'multi_correct'
                        ? (currentAnswer.selected_answer || '').split(',').includes(option)
                        : currentAnswer.selected_answer === option;
                      return (
                        <button key={option}
                          onClick={() => handleAnswer(currentQuestion.id, option, currentQuestion.question_type)}
                          className={`w-full text-left p-4 rounded-xl border-2 transition-all flex items-center gap-4
                            ${isSelected
                              ? 'bg-navy border-navy text-white shadow-exam'
                              : 'bg-white border-gray-200 text-gray-700 hover:border-navy/40 hover:bg-navy/5'}`}>
                          <span className={`w-7 h-7 rounded-lg flex items-center justify-center font-black text-sm flex-shrink-0
                            ${isSelected ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-500'}`}>
                            {option.toUpperCase()}
                          </span>
                          <span className="flex-1">{currentQuestion[`option_${option}`]}</span>
                          {isSelected && currentQuestion.question_type === 'multi_correct' && (
                            <span className="w-5 h-5 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">✓</span>
                          )}
                        </button>
                      );
                    })}
                    {currentQuestion.question_type === 'multi_correct' && (
                      <p className="text-xs text-gray-400 text-center">Select all that apply</p>
                    )}
                  </div>
                )}

                {/* Navigation */}
                <div className="flex items-center justify-between mt-8">
                  <button
                    disabled={currentIdx === 0}
                    onClick={() => navigateTo(currentIdx - 1)}
                    className="btn-secondary disabled:opacity-40 text-sm py-2">
                    <FiChevronLeft size={16} /> Previous
                  </button>

                  <button onClick={() => setShowNav(s => !s)} className="hidden lg:flex items-center gap-2 text-sm text-gray-500 hover:text-navy font-medium">
                    <FiGrid size={15} /> All Questions
                  </button>

                  {currentIdx < questions.length - 1 ? (
                    <button onClick={() => navigateTo(currentIdx + 1)} className="btn-primary text-sm py-2">
                      Next <FiChevronRight size={16} />
                    </button>
                  ) : (
                    <button onClick={() => setShowSubmit(true)} className="btn-success text-sm py-2">
                      <FiCheckCircle size={15} /> Finish & Submit
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}
